import z from "zod";

import {
  RADIO_ALIGNS,
  RADIO_INDICATOR_POSITIONS,
  RADIO_LAYOUTS,
} from "./constants";

const booleanAttribute = z
  .string()
  .optional()
  .transform((value) => value !== undefined && value !== "false");

export const radioGroupDataSchema = z.object({
  defaultValue: z
    .string()
    .optional()
    .describe(
      "Initially selected radio value. Read once, at mount, use `value` instead to change the selection after the initial render.",
    ),
  value: z
    .string()
    .optional()
    .describe(
      "Currently selected radio value. Settable at any time, e.g. `el.setAttribute('value', 'b')` or `el.setValue('b')`, to change the selection externally.",
    ),
  radios: z
    .array(
      z.object({
        value: z.string().describe("Unique identifier for this radio."),
        label: z.string().describe("Visible label shown next to the radio."),
        description: z
          .string()
          .optional()
          .describe(
            "Secondary line under the label, from the `description` attribute.",
          ),
      }),
    )
    .default([])
    .describe(
      "Radios parsed from the light DOM (<rwc-radio-group-item> elements inside <rwc-radio-group>), not a Twig-authored attribute.",
    ),
  name: z.string().optional(),
  orientation: z.enum(["horizontal", "vertical"]).optional(),
  layout: z
    .enum(RADIO_LAYOUTS)
    .optional()
    .describe(
      'Form factor: "text" scales the radios from the surrounding text and lays the group out inline, for a group that sits inside a sentence. "card" frames each option, "tile" stacks its content with the radio in a corner, "list" frames the group and separates the rows.',
    ),
  indicatorPosition: z
    .enum(RADIO_INDICATOR_POSITIONS)
    .optional()
    .describe("Side of the label the radio sits on."),
  align: z
    .enum(RADIO_ALIGNS)
    .optional()
    .describe(
      "Vertical alignment of the radio: on the title line, or centred on the row.",
    ),
  disabled: booleanAttribute,
  invalid: booleanAttribute,
  readOnly: booleanAttribute,
  required: booleanAttribute,
  onValueChange: z
    .custom<(value: string) => void>()
    .optional()
    .describe(
      "Internal callback bound by readRadioGroupSlotted, not a Twig-authored attribute, passed through the same raw-props pipeline.",
    ),
});

export type RadioGroupDataProps = z.infer<typeof radioGroupDataSchema>;
